<template>
  <AppRadioGroup
    v-if="isShowNav"
    v-model="model"
    :name="`tabs-${uniqueId}`"
    :options="options"
    :type="RADIO_GROUP_TYPES.TAB_LIKE"
    class="at-Nav"
  />

  <template v-for="item in options" :key="item.value">
    <slot
      v-if="item.value === model"
      :activate-tab="(v = item.value) => activateTab(v)"
      :is-tab-available="isTabAvailable"
      :name="getTabSlotName(item.value)"
    />
  </template>
</template>

<script setup>
import { isEmpty, isNull } from 'lodash'
import { computed, useId, watch, nextTick } from 'vue'

import { getTabSlotName } from '@/utils/app-tabs'
import { RADIO_GROUP_TYPES } from '@/utils/components-configurations/app-radio-group'
import { DATA_TYPES } from '@/utils/data-types'

import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'

defineOptions({
  name: 'AppTabs'
})

const props = defineProps({
  options: {
    type: Array,
    required: true
  },

  autoHideNav: {
    type: Boolean,
    default: true
  },

  autoSwitchTabs: {
    type: Boolean,
    default: true
  },

  hideNavPermanently: {
    type: Boolean
  }
})

const model = defineModel({
  required: true,
  validator: v =>
    [DATA_TYPES.STRING, DATA_TYPES.BOOLEAN, DATA_TYPES.NUMBER].includes(typeof v) || isNull(v),
  set: value => value
})

const uniqueId = useId()

const isShowNav = computed(() => {
  if (props.hideNavPermanently) return false

  if (!props.autoHideNav) return true

  return props.options.length > 1
})

watch(
  () => props.options,
  async newValue => {
    if (props.autoSwitchTabs) {
      if (isEmpty(newValue)) {
        await nextTick()
        model.value = null
        return
      }

      if (!newValue.find(tab => tab.value === model.value)) {
        await nextTick()
        model.value = props.options[0].value
      }
    }
  }
)

const isTabAvailable = value => {
  return props.options.some(tab => tab.value === value)
}

const activateTab = value => {
  if (value && isTabAvailable(value)) {
    model.value = value
  }
}
</script>

<style lang="scss" scoped>
.at-Nav {
  margin-bottom: var(--ob-at-margin-bottom, 8px);
}
</style>
